/**
 * The large-CSV grid (#981 stage 1, spec §7.4): a csv/tsv over LARGE_CSV_THRESHOLD_BYTES, read a
 * window at a time from its server-side working copy (`/api/csv/:fileId/…`) — never the whole file.
 *
 * - AG Grid's infinite row model: 200-row blocks, 5 kept; only the blocks on screen are read.
 * - A header click cycles the sort, the filter popup (the server's subset: text contains / equals /
 *   starts-with / not-blank, number = < > between) sets a filter: either becomes a server VIEW,
 *   built by a worker job (its progress shown) and then read a block at a time.
 * - The find bar asks the search endpoint (also a worker job) for the next matching cells. Hits are
 *   positions in the file's own order, so going to one clears a sort / filter first.
 * - While the working copy is built, a progress bar; a file the build can't parse, "can't be
 *   opened" with a download.
 * - No StatsBar (it would need every row).
 * - EDITING (stage 2, spec §8.2–§8.5) for a person who may write (`readOnly` false + a `room`): the
 *   session hook (`useLargeCsvSession`) joins the session room once and the fragment rooms of the
 *   rows in view; rows are read from those docs (else `/rows`), cells / rows / columns are edited
 *   there, and the toolbar holds Save / Discard / the conflict choices / "Saving…". Edits under a
 *   sort / filter view go to the row's own (fragment, source record); the view is then marked
 *   stale with a "Refresh view" button. Unsaved edits left on an earlier content of the file get a
 *   recovery bar. Read-only surfaces (the share view, a reader, a version) join no room at all.
 *
 * Used by the drive's large path, the share view's preview and CsvViewer's preview mode.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Search, X, ChevronUp, ChevronDown, FileWarning, Download, AlertCircle } from 'lucide-react'
import { AgGridReact } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type FilterModel,
  type GridApi,
  type ICellRendererParams,
  type IDatasource,
  type IGetRowsParams,
  type SortModelItem,
} from 'ag-grid-community'
import { useTranslation } from '@papan/i18n'
import { formatShortcut } from '@papan/core-ui/ui'
import { useFileIO, driveFileIO } from '@papan/files/client/file-io'
import { filesApi } from '@papan/files/client'
import {
  createLargeCsvAdapter,
  LARGE_CSV_MAX_ROWS_PER_READ,
  LargeCsvSwapPendingError,
  type CsvViewSpec,
  type LargeCsvAdapter,
  type LargeCsvCell,
  type LargeCsvNumberFilter,
  type LargeCsvOpenReady,
  type LargeCsvOpenResult,
  type LargeCsvRow,
  type LargeCsvTextFilter,
} from '../client/large-csv-adapter.js'
import { useLargeCsvSession, type CsvReloadWhy, type CsvRowTarget, type CsvSessionNotice, type CsvSessionRow, type LargeCsvSessionApi } from '../client/useLargeCsvSession.js'
import { markLoweredHere, wasLoweredHere, type CollabFileState } from '../client/useYjs.js'
import { dataGridTheme } from './data-grid-theme.js'
import { FileStateBanner } from './FileStateBanner.js'
import { LargeCsvEditBar } from './large-csv/LargeCsvEditBar.js'
import { LargeCsvStaleEditsBar } from './large-csv/LargeCsvStaleEditsBar.js'
import { rowIsCurrent, useLargeCsvEditing } from './large-csv/useLargeCsvEditing.js'
import { ROW_BUFFER, showRowAtTop, topVisibleRow } from './large-csv/grid-position.js'
import { LargeCsvColumnMenuItems, LargeCsvRowMenu } from './large-csv/LargeCsvMenus.js'

ModuleRegistry.registerModules([AllCommunityModule])

const BLOCK_SIZE = 200
const BLOCKS_CACHED = 5

export interface LargeCsvGridProps {
  fileId: string
  fileName?: string
  /** A saved version (the version preview): every read is of that version's content. */
  versionId?: string
  /** The surface's answer: true (the share view, a reader) = no rooms, no editing UI. */
  readOnly?: boolean
  /**
   * #1315 A — why the host made it read-only: `permission` (the person's permission answer says read), `other` (still
   * loading, an unreadable answer). Only a `permission` read-only grid tells "your access changed" from the tab's memory
   * of the room — never a false one while the host is still finding out.
   */
  readOnlyReason?: 'permission' | 'other'
  /** `${tenantId}:${fileId}` — the file's collab room; editing needs it (and `readOnly` false). */
  room?: string
  /** Where "Save as a copy" puts the copy (the file's folder). */
  parentId?: string | null
  /** The data layer (tests inject one). Default: the `/api/csv` adapter. */
  adapter?: LargeCsvAdapter
  /** The "download" of the can't-be-opened state. Default: the drive download, else the FileIO URL. */
  onDownload?: () => void
  /** Tests only: how re-opens a session asks for back off (default LARGE_CSV_RELOAD_POLICY). */
  reloadPolicy?: { max: number; baseMs: number }
  /** #981 QA 14: the session holds unsaved edits (this client's not yet acknowledged ones included) — told on every
   *  change, and `false` when the grid goes. The host keeps the grid while it is true, whatever the file's size. */
  onUnsavedChange?: (unsaved: boolean) => void
  /** #981 QA 14: the earlier-version bar acted (a copy saved, a Discard sent): the stale edits may be gone. */
  onStaleEditsChanged?: () => void
}

/**
 * A session that keeps asking to re-open (a room that closes on every open): each quick re-open waits
 * longer (`baseMs` doubling, ≤ 8 s); past `max` re-opens within RELOAD_WINDOW_MS the grid stops — the
 * session shows "Disconnected" with Re-open (the person's own retry, from scratch).
 */
export const LARGE_CSV_RELOAD_POLICY = { max: 5, baseMs: 250 }
const RELOAD_WINDOW_MS = 30_000
const RELOAD_MAX_DELAY_MS = 8_000
/**
 * How long a save's swap waits for the sort / filter view of the new content before switching anyway: past it the
 * grid switches (editing and Find resume) and the view finishes through the datasource's usual "building" / failed
 * notices — a hung view job never keeps the whole file paused.
 */
export const SWAP_VIEW_BOUND_MS = 120_000
/** While a swap waits for the rows on screen to be re-read, a read that has not come back is asked again this often. */
export const SWAP_REREAD_MS = 10_000
/**
 * How long a swap waits for the next session to be ready and the rows on screen to be its own (counted from when it
 * starts waiting for them). Past it — or at once when that session ends — the grid re-mounts as it did before #981's
 * no-reload save (the position kept): a read or a join that keeps failing never leaves "Saving…" up for good.
 */
export const SWAP_ROWS_BOUND_MS = 30_000
/**
 * The whole swap's watchdog, from its start: a rebase that never answers, a next session that never appears — any
 * wait the bounds above don't cover — ends in the same re-mount (position kept) rather than "Saving…" for good.
 */
export const SWAP_TOTAL_BOUND_MS = SWAP_VIEW_BOUND_MS + SWAP_ROWS_BOUND_MS

/**
 * Every LOADED row on screen was read from `base` (the session's content) — what a cell edit needs. A row still
 * loading has no data (it shows as loading; nothing to edit). Rows rendered as the buffer around the viewport count.
 */
export function visibleRowsCurrent(
  api: Pick<GridApi, 'getFirstDisplayedRowIndex' | 'getLastDisplayedRowIndex' | 'getDisplayedRowAtIndex'>,
  base: string,
  /** The editing session the rows must have been read through (a Discard keeps the base: only this tells). */
  via?: unknown,
): boolean {
  const first = api.getFirstDisplayedRowIndex()
  const last = api.getLastDisplayedRowIndex()
  for (let i = Math.max(0, first); i <= last; i++) {
    const data = api.getDisplayedRowAtIndex(i)?.data as GridRow | undefined
    if (data && (!rowIsCurrent(data, base) || (via !== undefined && data.via !== via))) return false
  }
  return true
}

/**
 * The NEW base's fragments of the rows on screen (#981 Task 8): the fragments of the old rows in the blocks the grid
 * shows, top block first. A save's swap joins them while the new sort / filter view is built, so the re-read after it
 * finds them joined. A wrong guess only costs those joins.
 *
 * Mapped by BASE ROW when both bases' row counts are known (#981 Task 13): an old row's place in the old base (its
 * fragment's first row + its record) falls in the new fragment holding that row — right whatever cut either base had
 * (the fragments move where rows are inserted or deleted; that only shifts the rows after it a little). An inserted
 * row (no record) counts as its fragment's end. Without row counts, by the fragment's place in the manifest.
 */
export function newBaseFragmentsOnScreen(
  api: Pick<GridApi, 'getFirstDisplayedRowIndex' | 'getLastDisplayedRowIndex' | 'getDisplayedRowAtIndex'>,
  oldFragments: { id: string; rows?: number }[],
  newFragments: { id: string; rows?: number }[],
  blockSize = BLOCK_SIZE,
): string[] {
  const first = Math.max(0, api.getFirstDisplayedRowIndex())
  const last = api.getLastDisplayedRowIndex()
  if (last < first) return []
  const place = new Map(oldFragments.map((f, i) => [f.id, i]))
  const byRow = oldFragments.every((f) => typeof f.rows === 'number') && newFragments.every((f) => typeof f.rows === 'number')
  const oldStart: number[] = []
  const newStart: number[] = []
  if (byRow) {
    let acc = 0
    for (const f of oldFragments) { oldStart.push(acc); acc += f.rows! }
    acc = 0
    for (const f of newFragments) { newStart.push(acc); acc += f.rows! }
  }
  /** The new fragment holding base row `row` (the last one past the end). */
  const newAt = (row: number): string | undefined => {
    let lo = 0
    let hi = newStart.length - 1
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1
      if (newStart[mid]! <= row) lo = mid
      else hi = mid - 1
    }
    return newFragments[lo]?.id
  }
  const out: string[] = []
  // the reads the swap makes are per block (each joins its first fragments): the whole blocks under the viewport
  const start = Math.floor(first / blockSize) * blockSize
  const end = (Math.floor(last / blockSize) + 1) * blockSize
  for (let i = start; i < end; i++) {
    const data = api.getDisplayedRowAtIndex(i)?.data as GridRow | undefined
    if (!data?.fragId) continue
    const k = place.get(data.fragId)
    if (k === undefined) continue
    const id = byRow
      ? newAt(oldStart[k]! + (typeof data.src === 'number' && data.src >= 0 ? data.src : oldFragments[k]!.rows!))
      : newFragments[k]?.id
    if (id && !out.includes(id)) out.push(id)
  }
  return out
}

/**
 * Where the person was when the session re-opened (a save, an unseal): the re-opened grid shows the same
 * view (sort, filter, frozen columns) from the same top row — never back at the top of the file.
 */
export interface LargeCsvGridPosition {
  row: number
  sortModel: SortModelItem[]
  filterModel: FilterModel | null
  frozen: string[]
}

export { topVisibleRow }

/** The find bar's marks: `query` (as searched, matched case-insensitively like the server) and the current hit. */
interface FindMark {
  query: string
  active: { row: number; col: string } | null
}

/** Not hidden by a `display: none` above it (a kept-alive tab that isn't the one shown). */
function isShown(el: HTMLElement): boolean {
  for (let n: HTMLElement | null = el; n; n = n.parentElement) if (getComputedStyle(n).display === 'none') return false
  return el.isConnected
}

export function isFindShortcut(e: Pick<KeyboardEvent, 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'key'>): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'f'
}

/** `value` split around each case-insensitive occurrence of `query`. */
export function findMarkParts(value: string, query: string): { text: string; mark: boolean }[] {
  const q = query.toLowerCase()
  if (!q) return [{ text: value, mark: false }]
  const lower = value.toLowerCase()
  const parts: { text: string; mark: boolean }[] = []
  let at = 0
  for (let i = lower.indexOf(q); i !== -1; i = lower.indexOf(q, at)) {
    if (i > at) parts.push({ text: value.slice(at, i), mark: false })
    parts.push({ text: value.slice(i, i + q.length), mark: true })
    at = i + q.length
  }
  if (at < value.length) parts.push({ text: value.slice(at), mark: false })
  return parts
}

/** A cell's text with the searched text marked (the current hit brighter) — the small grid's colours. */
function FindMarks({ value, query, active }: { value: string; query: string; active: boolean }) {
  const parts = findMarkParts(value, query)
  if (!parts.some((p) => p.mark)) return <>{value}</>
  return (
    <span data-testid={active ? 'large-csv-find-active' : undefined}>
      {parts.map((p, i) => p.mark
        ? (
            <mark key={i} data-testid="large-csv-find-mark" style={{ backgroundColor: active ? '#f59e0b' : '#854d0e', color: active ? '#000' : '#fef3c7', borderRadius: 2, padding: '0 1px' }}>
              {p.text}
            </mark>
          )
        : <span key={i}>{p.text}</span>)}
    </span>
  )
}

// ── AG Grid models → the server's view spec ────────────────────────────────

/** The number filters, offered beside the text ones on every column (the server types nothing). */
const NUMBER_OPTION: Record<string, LargeCsvNumberFilter['type']> = { numEq: 'eq', numLt: 'lt', numGt: 'gt', numBetween: 'between' }
const TEXT_OPTIONS = new Set<LargeCsvTextFilter['type']>(['contains', 'equals', 'startsWith', 'notBlank'])

interface FilterModelEntry {
  filterType?: string
  type?: string | null
  filter?: string | number | null
  filterTo?: string | number | null
}

/**
 * A sort or filter the PERSON changed shows the new view from its first row. The infinite model purges
 * its blocks on a view change and falls back to `infiniteInitialRowCount`, which clamps the scroll to
 * an arbitrary row near the first block — a person who sorted from the end of the file landed on row
 * ~180 of the sorted view. A change made by the grid's own code (`api`: going to a find hit leaves the
 * view and scrolls to the hit itself) is not moved.
 */
export function viewChangeScrollsToTop(source: string | undefined): boolean {
  return source === 'uiColumnSorted' || source === 'columnFilter' || source === 'quickFilter' || source === 'advancedFilter'
}

/** AG Grid's sort + filter models → a view spec; `null` = the file's own order (no view). */
export function gridModelsToViewSpec(sortModel: SortModelItem[], filterModel: Record<string, unknown> | null | undefined): CsvViewSpec | null {
  const spec: CsvViewSpec = {}
  const sort = sortModel
    .map((s) => ({ col: Number(s.colId), dir: s.sort }))
    .filter((s) => Number.isSafeInteger(s.col) && s.col >= 0)
  if (sort.length) spec.sort = sort
  const filter: NonNullable<CsvViewSpec['filter']> = {}
  for (const [colId, raw] of Object.entries(filterModel ?? {})) {
    const col = Number(colId)
    const m = raw as FilterModelEntry
    if (!Number.isSafeInteger(col) || col < 0 || !m || !m.type) continue
    if (TEXT_OPTIONS.has(m.type as LargeCsvTextFilter['type'])) {
      const type = m.type as LargeCsvTextFilter['type']
      if (type === 'notBlank') filter[col] = { type }
      else if (m.filter != null && String(m.filter) !== '') filter[col] = { type, value: String(m.filter) }
      continue
    }
    const numType = NUMBER_OPTION[m.type]
    if (!numType) continue
    const value = Number(m.filter)
    if (m.filter == null || String(m.filter).trim() === '' || !Number.isFinite(value)) continue
    if (numType === 'between') {
      const valueTo = Number(m.filterTo)
      if (m.filterTo == null || String(m.filterTo).trim() === '' || !Number.isFinite(valueTo)) continue
      filter[col] = { type: numType, value, valueTo }
    } else {
      filter[col] = { type: numType, value }
    }
  }
  if (Object.keys(filter).length) spec.filter = filter
  return spec.sort || spec.filter ? spec : null
}

/**
 * One grid row: its display index, output-order fields, (when editing) its identity, and the content hash it was
 * read at (`base`). After a save the session moves to the new hash; a row still held from the old one is never
 * edited (its fragment ids are the old base's) — it is read-only until re-read.
 */
type GridRow = Pick<LargeCsvRow, 'row' | 'fields'> & Partial<CsvRowTarget> & {
  base?: string | null
  /** The editing session that read it (its `readRows`): a swap ends only once the rows on screen are the new session's. */
  via?: unknown
  deleted?: true
}

export { rowIsCurrent }

/** What the datasource reads through while editing (the session hook's reads). */
export interface LargeCsvEditSource {
  currentTotal(): number
  joinForRows(start: number, end: number): Promise<void>
  joinForViewRows(rows: LargeCsvRow[]): Promise<void>
  readRows(start: number, count: number, fetchBase: (manifestStart: number, count: number) => Promise<LargeCsvRow[]>): Promise<CsvSessionRow[]>
  projectViewRows(rows: LargeCsvRow[]): CsvSessionRow[]
}

/**
 * The infinite model's datasource: each `getRows` is ONE block (AG Grid asks only for the blocks on
 * screen), read from the base order or from the view its sort / filter names. Views are asked once
 * per spec. `onViewProgress(null)` = no view is being built. `onViewFailed` = the sort / filter view
 * could not be built (the block is failed; the toolbar says so).
 *
 * The total (`totalRowsRef`) is read at each block: a save swaps the table to a new content in place.
 * Each row is stamped with the hash it was read at (`adapter.currentHash()`; none when the hash moved during
 * the read). A block that met a save landing (LargeCsvSwapPendingError) is failed quietly — the swap re-reads it.
 */
export function largeCsvDatasource(
  adapter: LargeCsvAdapter,
  totalRowsRef: { current: number },
  onViewProgress?: (progress: number | null) => void,
  /**
   * The view a block was read from (`provisional`: the old order after a save, #981 Task 9); `current` = no
   * provisional view was installed / dropped / forgotten since that read began (an older read says nothing about it).
   */
  onView?: (view: { viewId: string; rowCount: number; provisional?: true } | null, current: boolean) => void,
  onViewFailed?: () => void,
  /** Editing: rows come through the session (joined fragment docs, else `/rows` by manifest index). */
  edit?: () => LargeCsvEditSource | null,
  /** A block's rows reached the grid (after the grid applied them). */
  onRowsLoaded?: () => void,
  /**
   * #981 Task 9: the view the grid shows NOW. `pending`: a save's renumbered columns are not applied yet (the grid's
   * sort / filter still name the OLD ids) — every read uses `spec`; otherwise a read whose own models differ from
   * `spec` is stale and is dropped. Undefined = can't tell (the read's own models).
   */
  viewSpecNow?: () => { spec: CsvViewSpec | null; pending: boolean } | undefined,
): IDatasource & {
  forgetViews(): void
  /** Asks for the view of `spec` now (the swap after a save), progress to `onProgress`; later reads reuse it. */
  prepareView(spec: CsvViewSpec, onProgress?: (progress: number) => void): ReturnType<LargeCsvAdapter['view']>
  /**
   * #981 Task 9: reads of `spec` use `view` (the old order over the new rows, after a save that moved no row) until
   * it is dropped — its own reads failing fall back to the real view.
   */
  setProvisional(spec: CsvViewSpec, view: ProvisionalView): void
  /** Drops `view` for `spec`; false when it was no longer the one there (forgotten, replaced, dropped). */
  dropProvisional(spec: CsvViewSpec, view: ProvisionalView): boolean
} {
  const views = new Map<string, ReturnType<LargeCsvAdapter['view']>>()
  const provisional = new Map<string, ProvisionalView>()
  /** Bumped whenever the provisional views change: a read begun before says nothing about them. */
  let epoch = 0
  const ask = (spec: CsvViewSpec, onProgress: (progress: number) => void) => {
    const key = JSON.stringify(spec)
    const pending = adapter.view(spec, onProgress)
    views.set(key, pending)
    void pending.then((v) => { if ('failed' in v && views.get(key) === pending) views.delete(key) }, () => { if (views.get(key) === pending) views.delete(key) })
    return pending
  }
  return {
    rowCount: undefined,
    /** "Refresh view": the next read asks the server for each view again (rebuilt from the saved content). */
    forgetViews: () => { views.clear(); provisional.clear(); epoch++ },
    // the same spec already asked (and not settled as failed): that job, never a second one
    prepareView: (spec, onProgress) => views.get(JSON.stringify(spec)) ?? ask(spec, (p) => onProgress?.(p)),
    setProvisional: (spec, view) => { provisional.set(JSON.stringify(spec), view); epoch++ },
    dropProvisional: (spec, view) => {
      const key = JSON.stringify(spec)
      if (provisional.get(key) !== view) return false
      provisional.delete(key)
      epoch++
      return true
    },
    getRows(params: IGetRowsParams) {
      const own = gridModelsToViewSpec(params.sortModel ?? [], params.filterModel as Record<string, unknown>)
      const now = viewSpecNow?.()
      // a read asked under models the grid no longer has (queued before a renumbering was applied): its block is
      // re-asked under the current ones — it neither starts a view nor speaks for the view shown
      if (now && !now.pending && JSON.stringify(now.spec) !== JSON.stringify(own)) { params.failCallback(); return }
      const spec = now?.pending ? now.spec : own
      void (async () => {
        // A read that spans a hash change (a save's swap) or a change of editing session (a swap / reset in place)
        // can't say which content it holds: stamped none (read-only until the swap's re-read), never the new hash
        // over rows the old content or the old session's docs gave. A session is known by its `readRows` (stable per
        // session; the hook's API object changes with every state change).
        const readAt = adapter.currentHash()
        let viewId: string | undefined
        const session = edit?.() ?? null
        const stamp = () => {
          const now = adapter.currentHash()
          return now === readAt && (edit?.() ?? null)?.readRows === session?.readRows ? now : null
        }
        let last = session ? session.currentTotal() : totalRowsRef.current
        const readEpoch = epoch
        const key = spec ? JSON.stringify(spec) : ''
        /** The real view of `spec` (asked once per spec), or a thrown failure (said by `onViewFailed`). */
        const realView = async () => {
          let pending = views.get(key)
          if (!pending) {
            onViewProgress?.(0)
            pending = ask(spec!, (p) => onViewProgress?.(p))
          }
          const v = await pending.catch((err: unknown) => {
            onViewProgress?.(null)
            if (!(err instanceof LargeCsvSwapPendingError)) onViewFailed?.()
            throw err
          })
          onViewProgress?.(null)
          if ('failed' in v) {
            onViewFailed?.()
            throw new Error('view failed')
          }
          return v
        }
        // #981 Task 9: the old order after a save that moved no row, while the real view is built
        let prov = spec ? provisional.get(key) : undefined
        if (prov) {
          viewId = prov.viewId
          last = prov.rowCount
          onView?.({ ...prov, provisional: true }, epoch === readEpoch)
        } else if (spec) {
          const v = await realView()
          viewId = v.viewId
          last = v.rowCount
          onView?.(v, epoch === readEpoch)
        } else {
          onView?.(null, epoch === readEpoch)
        }
        const start = params.startRow
        if (session && !viewId) {
          // the fragments of these rows are joined first: their live counts place the rows
          await session.joinForRows(start, params.endRow)
          last = session.currentTotal()
          const count = Math.min(params.endRow - start, LARGE_CSV_MAX_ROWS_PER_READ, Math.max(0, last - start))
          const rows = count > 0 ? await session.readRows(start, count, (ms, n) => adapter.rows(ms, n)) : []
          const base = stamp()
          params.successCallback(rows.map((r) => toGridRow(r, base, session.readRows)), last)
          if (onRowsLoaded) setTimeout(onRowsLoaded, 0)
          return
        }
        const countOf = () => Math.min(params.endRow - start, LARGE_CSV_MAX_ROWS_PER_READ, Math.max(0, last - start))
        let rows: LargeCsvRow[]
        try {
          rows = countOf() > 0 ? await adapter.rows(start, countOf(), viewId) : []
        } catch (err) {
          if (!prov || err instanceof LargeCsvSwapPendingError) throw err
          // the old order can't be read any more (its copy swept): the real view, as without a provisional one
          if (provisional.get(key) === prov) { provisional.delete(key); epoch++ }
          prov = undefined
          const v = await realView()
          viewId = v.viewId
          last = v.rowCount
          onView?.(v, true)
          rows = countOf() > 0 ? await adapter.rows(start, countOf(), viewId) : []
        }
        // a view's rows show through their fragments' docs too (unsaved edits), ≤ the join cap
        if (session) await session.joinForViewRows(rows)
        const base = stamp()
        params.successCallback(session ? session.projectViewRows(rows).map((r) => toGridRow(r, base, session.readRows)) : rows.map((r) => ({ row: r.row, fields: r.fields, base })), last)
        if (onRowsLoaded) setTimeout(onRowsLoaded, 0)
      })().catch(() => {
        onViewProgress?.(null)
        params.failCallback()
      })
    },
  }
}

/** #981 Task 9: a view served under a provisional id (the old base's order over the new base's rows). */
export interface ProvisionalView { viewId: string; rowCount: number }

/**
 * #981 Task 9 — a save writes the session's columns in display order: column i of the new base is the session's
 * column i. The map old slot → new slot; `'identity'` when no column moved; null when it can't be told (the counts
 * differ — then nothing is remapped and no provisional view is used).
 */
export function savedColumnSlots(oldCols: readonly { slot: string }[] | null | undefined, newColumnCount: number): Map<string, string> | 'identity' | null {
  if (!oldCols || oldCols.length !== newColumnCount) return null
  if (oldCols.every((c, i) => c.slot === String(i))) return 'identity'
  return new Map(oldCols.map((c, i) => [c.slot, String(i)]))
}

/**
 * The person's sort / filter / frozen columns in the NEW base's column ids (`savedColumnSlots`). `lost`: a sort or
 * filter named a column the save does not have (its order then is not the old view's).
 */
export function remapGridPosition(at: LargeCsvGridPosition, slots: Map<string, string>): { at: LargeCsvGridPosition; lost: boolean } {
  let lost = false
  const sortModel = at.sortModel.flatMap((s) => {
    const id = slots.get(s.colId)
    if (id === undefined) { lost = true; return [] }
    return [{ ...s, colId: id }]
  })
  let filterModel: FilterModel | null = null
  if (at.filterModel) {
    filterModel = {}
    for (const [k, v] of Object.entries(at.filterModel)) {
      const id = slots.get(k)
      if (id === undefined) { lost = true; continue }
      filterModel[id] = v
    }
  }
  return { at: { row: at.row, sortModel, filterModel, frozen: at.frozen.flatMap((f) => slots.get(f) ?? []) }, lost }
}

/** A view row this client deleted: struck through until the save drops it from the view. */
const ROW_CLASS_RULES = { 'large-csv-row-deleted': (p: { data?: GridRow }) => !!p.data?.deleted }

const toGridRow = (r: CsvSessionRow, base: string | null, via: unknown): GridRow => ({ row: r.row, fields: r.fields, fragId: r.fragId, src: r.src, item: r.item, base, via, ...(r.deleted ? { deleted: true as const } : {}) })

// ── The component ──────────────────────────────────────────────────────────

type OpenState =
  | { kind: 'opening'; progress: number | null }
  | { kind: 'ready'; summary: LargeCsvOpenReady }
  | { kind: 'unparseable' }
  | { kind: 'error' }

const percent = (p: number) => Math.max(0, Math.min(100, Math.round(p * 100)))

export function LargeCsvGrid({ fileId, fileName, versionId, readOnly, readOnlyReason, room, parentId, adapter: injected, onDownload, reloadPolicy, onUnsavedChange, onStaleEditsChanged }: LargeCsvGridProps) {
  // #981 QA 14: the grid gone, it holds nothing
  const onUnsavedOuterRef = useRef(onUnsavedChange)
  onUnsavedOuterRef.current = onUnsavedChange
  useEffect(() => () => onUnsavedOuterRef.current?.(false), [])
  const { t } = useTranslation('collab')
  const fileIO = useFileIO()
  const [state, setState] = useState<OpenState>({ kind: 'opening', progress: null })
  /**
   * #1315 A — the read-only grid runs no session, so its banner comes from what this tab knows about the room
   * (`wasLoweredHere`). The host's permission refetch can beat the server's frame: the grid then turns read-only while
   * its live room had joined writable — lowered, said here (`markLoweredHere`), but only when the host's reason is the
   * permission itself.
   */
  const liveRoom = !!room && !versionId
  const wasReadOnly = useRef(readOnly)
  const [, bumpBanner] = useState(0)
  useLayoutEffect(() => {
    const was = wasReadOnly.current
    wasReadOnly.current = readOnly
    if (readOnly && !was && liveRoom && readOnlyReason === 'permission') { markLoweredHere(room!); bumpBanner((n) => n + 1) }
  }, [readOnly, liveRoom, room, readOnlyReason])
  const readOnlyBanner: CollabFileState = readOnly && liveRoom && readOnlyReason === 'permission' && wasLoweredHere(room!) ? { kind: 'read-only' } : null
  const [attempt, setAttempt] = useState(0)
  /** The last save answer, handed across a re-open (a failed save's unseal reloads right after answering). */
  const [carried, setCarried] = useState<CsvSessionNotice | null>(null)
  /** Re-opens the session asked for lately (their times), and whether the grid gave up on them. */
  const recentReloads = useRef<number[]>([])
  const [gaveUp, setGaveUp] = useState(false)
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (reloadTimer.current) clearTimeout(reloadTimer.current) }, [])
  const policy = reloadPolicy ?? LARGE_CSV_RELOAD_POLICY
  const reopenNow = (carry: CsvSessionNotice | null) => { setCarried(carry); setAttempt((n) => n + 1) }
  /** Where the table was when its session asked to re-open (shown again by the next table). */
  const position = useRef<LargeCsvGridPosition | null>(null)
  /** Counts a re-open (or an in-place reset) in the back-off window; true while still under `max`. */
  const noteReload = (): boolean => {
    const now = Date.now()
    const recent = recentReloads.current.filter((at) => now - at < RELOAD_WINDOW_MS)
    recent.push(now)
    recentReloads.current = recent
    return recent.length <= policy.max
  }
  /**
   * The session asked to re-open (a bare `reload` from an older server, an outside change, a swap that could not
   * be done in place): backed off when it keeps asking, given up past `max`. A save / a failed save / a Discard the
   * table switches in place never comes here.
   */
  const onSessionReload = (carry: CsvSessionNotice | null, at: LargeCsvGridPosition | null, counted = false) => {
    position.current = at
    // a hold for a save this table saw `saving` for ends here: the next table opens afresh
    adapter.release()
    // (an in-place reset refused for coming too often was counted already)
    if (!counted) noteReload()
    const recent = recentReloads.current
    if (recent.length > policy.max) {
      // No re-open: the table re-mounts (its key) on an ENDED session — no rooms, Re-open offered.
      setCarried(carry)
      setGaveUp(true)
      return
    }
    const delay = recent.length <= 1 ? 0 : Math.min(RELOAD_MAX_DELAY_MS, policy.baseMs * 2 ** (recent.length - 2))
    if (reloadTimer.current) clearTimeout(reloadTimer.current)
    if (delay === 0) { reopenNow(carry); return }
    reloadTimer.current = setTimeout(() => { reloadTimer.current = null; reopenNow(carry) }, delay)
  }
  /** The person's Re-open: from scratch. */
  const onReopen = () => {
    position.current = null
    adapter.release()
    recentReloads.current = []
    setGaveUp(false)
    reopenNow(null)
  }

  /** Bumped when the file changed under the grid (not by a save this grid swapped to): the table re-mounts. */
  const [outsideChange, setOutsideChange] = useState(0)
  const adapter = useMemo(
    () => injected ?? createLargeCsvAdapter(fileId, {
      versionId,
      // The file changed under the grid: show the new content from the top.
      onReopen: (summary) => { position.current = null; setOutsideChange((n) => n + 1); setState({ kind: 'ready', summary }) },
    }),
    [injected, fileId, versionId],
  )

  useEffect(() => {
    let live = true
    setState({ kind: 'opening', progress: null })
    adapter.open((p) => { if (live) setState({ kind: 'opening', progress: p }) }).then(
      (r) => {
        if (!live) return
        if ('failed' in r) setState({ kind: r.failed === 'unparseable' ? 'unparseable' : 'error' })
        else setState({ kind: 'ready', summary: r })
      },
      () => { if (live) setState({ kind: 'error' }) },
    )
    return () => { live = false }
  }, [adapter, attempt])

  const download = useCallback(() => {
    if (onDownload) return onDownload()
    if (fileIO === driveFileIO && !versionId) return void filesApi.download([fileId])
    window.open(fileIO.getStreamingUrl(fileId, { versionId, fileName }), '_blank', 'noopener')
  }, [onDownload, fileIO, fileId, versionId, fileName])

  if (state.kind === 'opening') {
    if (state.progress === null) {
      return (
        <div className="flex-1 flex items-center justify-center" data-testid="large-csv-opening">
          <Loader2 className="animate-spin themed-text-muted" size={24} />
        </div>
      )
    }
    const pct = percent(state.progress)
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="large-csv-building">
        <div className="w-72 max-w-full px-6 text-center">
          <p className="text-sm mb-2">{t('largeCsv.building', { percent: pct })}</p>
          <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} className="h-1.5 rounded themed-bg-tertiary overflow-hidden">
            <div className="h-full themed-btn-primary transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
    )
  }

  if (state.kind === 'unparseable') {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="large-csv-unparseable">
        <div className="text-center max-w-md px-6">
          <FileWarning size={48} className="mx-auto mb-4 opacity-50" />
          <p className="themed-text mb-1">{t('largeCsv.unparseableTitle')}</p>
          <p className="text-sm opacity-60 mb-4">{t('largeCsv.unparseableBody')}</p>
          <button onClick={download} className="px-4 py-2 text-sm themed-btn-primary rounded inline-flex items-center gap-2" data-testid="large-csv-download">
            <Download size={14} />
            {t('largeCsv.download')}
          </button>
        </div>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted" data-testid="large-csv-error">
        <div className="text-center max-w-md px-6">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm mb-3">{t('largeCsv.errorTitle')}</p>
          <button onClick={() => setAttempt((n) => n + 1)} className="px-3 py-1 text-xs themed-btn-secondary rounded" data-testid="large-csv-retry">
            {t('largeCsv.retry')}
          </button>
        </div>
      </div>
    )
  }

  // A re-open (`attempt`) or an outside change is a new table. A save, a failed save and a Discard are not: the
  // table switches its content / session in place (its `summary` is its own state from here).
  return (
    <LargeCsvTable
      key={`${attempt}:${outsideChange}:${gaveUp ? 'ended' : 'live'}`}
      adapter={adapter}
      summary={state.summary}
      fileId={fileId}
      fileName={fileName}
      parentId={parentId ?? null}
      room={!readOnly && !versionId ? room : undefined}
      readOnlyBanner={readOnlyBanner}
      notice={carried}
      initialPosition={position.current}
      onReload={onSessionReload}
      onInPlaceReset={noteReload}
      // a save switched the table to new content: a later re-mount (give-up, Re-open) shows THAT content
      onContentSwapped={(summary) => setState({ kind: 'ready', summary })}
      onReopen={onReopen}
      gaveUp={gaveUp}
      onUnsavedChange={onUnsavedChange}
      onStaleEditsChanged={onStaleEditsChanged}
    />
  )
}

interface LargeCsvTableProps {
  adapter: LargeCsvAdapter
  onUnsavedChange?: (unsaved: boolean) => void
  onStaleEditsChanged?: () => void
  summary: LargeCsvOpenReady
  fileId: string
  fileName?: string
  parentId: string | null
  /** Set only when this person may edit. */
  room?: string
  /** #1315 A: the banner while no session runs (the grid read-only after the person's access was lowered here). */
  readOnlyBanner?: CollabFileState
  /** A save answer carried over from the session before a re-open. */
  notice: CsvSessionNotice | null
  /** Where the table before this one was when its session re-opened. */
  initialPosition: LargeCsvGridPosition | null
  /** Re-mount (today's path): a bare reload, an outside change, a swap that could not be done in place. */
  onReload: (carry: CsvSessionNotice | null, at: LargeCsvGridPosition | null, counted?: boolean) => void
  /** The table switched to a save's content in place (the next table, if one is mounted, starts from it). */
  onContentSwapped: (summary: LargeCsvOpenReady) => void
  /** An in-place reset (Discard / a failed save) is about to happen: false = too many lately, re-mount instead (backs off). */
  onInPlaceReset: () => boolean
  /** The person's Re-open (after the session ended). */
  onReopen: () => void
  /** The grid stopped re-opening a session that kept closing: it starts ended (disconnected). */
  gaveUp: boolean
}

/**
 * Why the table is switching in place: `save` — a save landed (new content hash; the view, if any, is rebuilt for
 * it while the old rows stay on screen); `reset` — a Discard or a failed save (same base, rooms re-joined). Editing
 * and Find pause until the next session is in place.
 */
type Swapping = null | 'save' | 'reset'

function LargeCsvTable({ adapter, summary: openedWith, fileId, fileName, parentId, room, readOnlyBanner = null, notice: carriedIn, initialPosition, onReload, onInPlaceReset, onContentSwapped, onReopen, gaveUp, onUnsavedChange, onStaleEditsChanged }: LargeCsvTableProps) {
  const { t } = useTranslation('collab')
  const gridRef = useRef<AgGridReact<GridRow>>(null)
  /** The content shown: the open's, then each save's (switched in place). */
  const [summary, setSummary] = useState(openedWith)
  const summaryRef = useRef(summary)
  summaryRef.current = summary
  const totalRowsRef = useRef(summary.totalRows)
  totalRowsRef.current = summary.totalRows
  /** The count AG Grid starts from (its prop is read once): the first content's. Later totals come from block answers. */
  const [initialRowCount] = useState(() => Math.max(1, openedWith.totalRows))
  /** Bumped by an in-place reset: a fresh session on the same base. */
  const [sessionGen, setSessionGen] = useState(0)
  const [swapping, setSwappingState] = useState<Swapping>(null)
  const swappingRef = useRef<Swapping>(null)
  const setSwapping = (s: Swapping) => { swappingRef.current = s; setSwappingState(s) }
  /** The save answer shown by the next session (carried across a reload, in place or not). */
  const [notice, setNotice] = useState(carriedIn)
  /** The new view being built after a save (0..1); the old view's rows stay on screen meanwhile. */
  const [viewUpdating, setViewUpdating] = useState<number | null>(null)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  /** A swap waits for the next session: the grid then re-reads its blocks from `row` (the top row's index). */
  const awaitingSession = useRef<{ id: number; from: unknown; row: number; clearStale: boolean; purge: boolean; carry: CsvSessionNotice | null } | null>(null)
  /** A save's swap with a view: the new base's fragments of the rows on screen, joined once its next session is up. */
  const prejoin = useRef<{ id: number; from: unknown; frags: string[] } | null>(null)
  /** The current swap's id: a later swap (a collaborator's save during this one) supersedes an earlier one's waits. */
  const swapId = useRef(0)
  /** The swap under way (its id, and the save answer a fallback re-mount carries); null = none. */
  const currentSwap = useRef<{ id: number; carry: CsvSessionNotice | null } | null>(null)
  const totalBound = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * #981 Task 9: a save that added / deleted a column renumbers the columns — the sort, filter and frozen columns in
   * the new ids (`at`), applied once the grid has the new base's `count` columns.
   */
  const pendingColumns = useRef<{ id: number; at: LargeCsvGridPosition; count: number } | null>(null)
  /** The view in the NEW column ids while they are not applied to the grid yet; `undefined` = none pending. */
  const pendingViewSpec = (): CsvViewSpec | null | undefined => {
    const p = pendingColumns.current
    return p ? gridModelsToViewSpec(p.at.sortModel, p.at.filterModel as Record<string, unknown> | null) : undefined
  }
  /** The view the grid reads now (a pending renumbering's, else the grid's own sort / filter). */
  const currentViewSpec = (): CsvViewSpec | null => {
    const pending = pendingViewSpec()
    if (pending !== undefined) return pending
    const now = positionNow()
    return now ? gridModelsToViewSpec(now.sortModel, now.filterModel as Record<string, unknown> | null) : null
  }
  /** Drops every wait, timer and indicator of the swap under way (it ended, was abandoned or superseded). */
  const clearSwapWaits = () => {
    awaitingSession.current = null
    prejoin.current = null
    awaitingRows.current = null
    if (rowsBound.current) { clearTimeout(rowsBound.current); rowsBound.current = null }
    if (totalBound.current) { clearTimeout(totalBound.current); totalBound.current = null }
    setViewUpdating(null)
  }
  /** Starts a swap: every wait of an earlier one is dropped (its completion can never end this one); armed with the watchdog. */
  const beginSwap = (kind: 'save' | 'reset', carry: CsvSessionNotice | null): number => {
    clearSwapWaits()
    pendingColumns.current = null
    const id = ++swapId.current
    currentSwap.current = { id, carry }
    setSwapping(kind)
    totalBound.current = setTimeout(() => { totalBound.current = null; abandonSwapRef.current(id) }, SWAP_TOTAL_BOUND_MS)
    return id
  }
  /** Swap `id` is over (editing and Find back) — a no-op for a swap that is no longer the current one. */
  const endSwap = (id: number) => {
    if (currentSwap.current?.id !== id) return
    clearSwapWaits()
    currentSwap.current = null
    setSwapping(null)
  }
  /** Swap `id` can't finish (its rebase / session / rows never came, or the session ended): re-mount, the position kept. */
  const abandonSwap = (id: number) => {
    const c = currentSwap.current
    if (!c || c.id !== id || !mounted.current) return
    endSwap(id)
    onReload(c.carry, positionNow())
  }
  const abandonSwapRef = useRef(abandonSwap)
  /**
   * The swap's re-read is under way: it ends (editing and Find back) only when the next session is ready AND every
   * loaded row on screen was read from it — never a cell that looks editable but silently refuses (#981 e2e: a
   * double-click right after Save was ignored for ~1.5 s while the old-content rows and an unready session stayed).
   */
  const awaitingRows = useRef<{ id: number } | null>(null)
  const rowsBound = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (rowsBound.current) clearTimeout(rowsBound.current)
    if (totalBound.current) clearTimeout(totalBound.current)
  }, [])
  const editable = !!room && !!summary.fragments
  /** Read once: the position this table opens at (a later prop change is the next table's). */
  const [opensAt] = useState(initialPosition)
  const [frozen, setFrozen] = useState<Set<string>>(() => new Set(opensAt?.frozen ?? []))
  const frozenRef = useRef(frozen)
  frozenRef.current = frozen
  const [initialGridState] = useState(() => opensAt
    ? { sort: { sortModel: opensAt.sortModel }, ...(opensAt.filterModel ? { filter: { filterModel: opensAt.filterModel } } : {}) }
    : undefined)
  const positionNow = (): LargeCsvGridPosition | null => {
    const api = gridRef.current?.api
    return api && !api.isDestroyed()
      ? {
          row: topVisibleRow(api),
          sortModel: api.getColumnState().filter((c) => c.sort).sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
            .map((c) => ({ colId: c.colId, sort: c.sort! })),
          filterModel: api.getFilterModel() ?? null,
          frozen: [...frozenRef.current],
        }
      : null
  }

  /**
   * A save landed (`base` = the new content): switch to it in place. Editing and Find pause; the blocks on screen
   * stay. The adapter moves to the new hash; with a sort / filter its view is built for the new hash first (the old
   * view's rows readable meanwhile) — the next session joining the new base's rooms meanwhile; without one the content
   * switches at once. Either way the blocks are re-read once that session is up. A rebase that fails takes today's
   * re-mount.
   */
  const swapToSaved = async (carry: CsvSessionNotice | null, why: Extract<CsvReloadWhy, { kind: 'saved' }>) => {
    const id = beginSwap('save', carry)
    // what the person was looking at, on the OLD base: its hash and the session's columns (the saved file's, in order)
    const oldHash = summaryRef.current.contentHash
    const oldCols = sessionRef.current?.columns
    const r = await adapter.rebase().catch((): LargeCsvOpenResult => ({ failed: 'error' }))
    // unmounted, or a later swap took over (or the watchdog gave this one up)
    if (!mounted.current || currentSwap.current?.id !== id) return
    if ('failed' in r) {
      abandonSwap(id)
      return
    }
    // the views of the old content are no good on the new one (the next reads ask again)
    datasource.forgetViews()
    const before = positionNow()
    /** The view as the OLD base had it (its column ids): the order on screen. */
    const oldSpec = before ? gridModelsToViewSpec(before.sortModel, before.filterModel as Record<string, unknown> | null) : null
    // #981 Task 9: a save writes the session's columns in display order — a column added or deleted renumbers them.
    // The sort / filter / frozen columns move to the new ids once the new columns are in (`pendingColumns`).
    const slots = savedColumnSlots(oldCols, r.columnCount)
    let at = before
    let lost = false
    if (before && slots instanceof Map) {
      const moved = remapGridPosition(before, slots)
      at = moved.at
      lost = moved.lost
      pendingColumns.current = { id, at: moved.at, count: r.columnCount }
      // find hits name the OLD column ids: the next Enter searches the saved content again
      resetFind()
    }
    const spec = at ? gridModelsToViewSpec(at.sortModel, at.filterModel as Record<string, unknown> | null) : null
    /** The session the rows on screen were read through: the swap re-reads them once the next one is up. */
    const from = sessionRef.current?.readRows
    let viewTimedOut = false
    if (spec) {
      // #981 Task 8: the next session starts NOW, while the view is built — it joins its room, and the new base's
      // fragments of the rows on screen (`prejoin`), so the re-read after the view finds them joined. On stagetest
      // these joins took ~4.7 s after the view (each fragment doc ~2 MB); the swap's rules are unchanged: editing and
      // Find stay off, and the swap ends only once the rows on screen are re-read through that session.
      const api = gridRef.current?.api
      prejoin.current = editable && api && !api.isDestroyed()
        ? { id, from, frags: newBaseFragmentsOnScreen(api, summaryRef.current.fragments ?? [], r.fragments ?? []) }
        : null
      setNotice(carry)
      setSummary(r)
      onContentSwapped(r)
      setViewUpdating(0)
      /** Past the bound: the rest of the build is shown as the datasource's own "building". */
      let building = false
      const prepared = datasource.prepareView(spec, (p) => {
        if (!mounted.current) return
        // past the bound the build is the datasource's (its reads wait on it): its progress shows as "building"
        if (building) setViewProgress(p)
        // before it, "Updating sort…" belongs to this swap only — a superseded / abandoned one says nothing more (the
        // next session is live during this phase: a later save it hears supersedes this swap)
        else if (currentSwap.current?.id === id) setViewUpdating(p)
      })
      // #981 Task 9: the save moved no row — the OLD base's view orders the new rows too. Asked beside the real view;
      // if it answers first, editing resumes on it (the "not re-sorted" notice up) while the real view is built. The
      // order the person saw is the old base's spec (its column ids); none answered = the wait as before.
      const provisional = why.sameRows && r.contentHash === why.base && oldSpec && slots !== null && !lost && adapter.provisionalView
        ? adapter.provisionalView(oldSpec, oldHash).catch(() => null)
        : Promise.resolve(null)
      let timer: ReturnType<typeof setTimeout> | undefined
      const v = await Promise.race([
        // a later save landing meanwhile (the next session heard it: reads are held) is that save's swap, no failure
        prepared.catch((err: unknown): { failed: 'error' | 'superseded' } => ({ failed: err instanceof LargeCsvSwapPendingError ? 'superseded' : 'error' })),
        // none (no record, the old view gone, an older server): only the real view or the bound end the wait
        provisional.then((p) => (p ? { provisional: p } : new Promise<never>(() => {}))),
        new Promise<'timeout'>((resolve) => { timer = setTimeout(() => resolve('timeout'), SWAP_VIEW_BOUND_MS) }),
      ])
      clearTimeout(timer)
      if (!mounted.current || currentSwap.current?.id !== id) return
      setViewUpdating(null)
      if (v !== 'timeout' && 'provisional' in v) {
        showProvisional(spec, v.provisional, prepared)
        awaitingSession.current = { id, from, row: positionNow()?.row ?? at?.row ?? 0, clearStale: false, purge: false, carry }
        continueSwapRef.current()
        return
      }
      if (v === 'timeout') {
        // switch now; the reads of this view wait for it (and say so), its answer or failure is shown as usual
        building = true
        viewTimedOut = true
        setViewProgress(0)
        void prepared.finally(() => { if (mounted.current) setViewProgress(null) }).catch(() => {})
      } else if ('failed' in v && v.failed === 'error') {
        setViewFailed(true)
      }
    }
    // past the bound the old view's rows are the old content's and its new view is still building: let them go
    // (the rows show loading under the "building" notice, like any view being built) rather than keep them on screen
    const purge = viewTimedOut
    awaitingSession.current = { id, from, row: positionNow()?.row ?? at?.row ?? 0, clearStale: true, purge, carry }
    if (spec) {
      // the next session is already up (or its effect picks this up when it is)
      continueSwapRef.current()
      return
    }
    setNotice(carry)
    setSummary(r)
    onContentSwapped(r)
  }

  /**
   * #981 Task 9: the swap ends on the provisional view (the old order over the new rows; the notice says so);
   * the real view `prepared` replaces it in place when built — the top row's index kept — unless the person moved to
   * another sort / filter, pressed Refresh or a later save took over meanwhile (then its answer is not used here).
   */
  const showProvisional = (spec: CsvViewSpec, view: ProvisionalView, prepared: ReturnType<LargeCsvAdapter['view']>) => {
    datasource.setProvisional(spec, view)
    edit.showProvisionalView()
    const done = (failed: boolean) => {
      if (!mounted.current || !datasource.dropProvisional(spec, view)) return
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      // the person is on another sort / filter now: nothing to move (a renumbering not applied yet reads `spec` too)
      if (JSON.stringify(currentViewSpec()) !== JSON.stringify(spec)) return
      if (failed) setViewFailed(true)
      const row = topVisibleRow(api)
      api.refreshInfiniteCache()
      showRowAtTop(api, row)
    }
    prepared.then(
      (real) => done('failed' in real),
      // a later save landing: its swap forgets this view (nothing to do here)
      (err: unknown) => { if (!(err instanceof LargeCsvSwapPendingError)) done(true) },
    )
  }

  /**
   * A Discard (`reset`) or a failed save (`unsealed`): the same base, its rooms re-joined (clean, or with the kept
   * edits) by a fresh session; the views are still valid; the blocks on screen are re-read. If the adapter already
   * moved to another hash (a re-open that began before the save's hold), or resets keep coming, re-mount instead.
   */
  const resetInPlace = (carry: CsvSessionNotice | null, why: CsvReloadWhy) => {
    adapter.release()
    const h = adapter.currentHash()
    if (h !== null && h !== summaryRef.current.contentHash) {
      onReload(carry, positionNow())
      return
    }
    // counted once: a refused one is not counted again by the re-mount
    if (!onInPlaceReset()) {
      onReload(carry, positionNow(), true)
      return
    }
    const id = beginSwap('reset', carry)
    awaitingSession.current = { id, from: sessionRef.current?.readRows, row: positionNow()?.row ?? 0, clearStale: why.kind === 'reset', purge: false, carry }
    setNotice(carry)
    setSessionGen((n) => n + 1)
  }

  const reloadHere = (carry: CsvSessionNotice | null, why: CsvReloadWhy) => {
    if (why.kind === 'saved') { void swapToSaved(carry, why); return }
    if (why.kind === 'reset' || why.kind === 'unsealed') { resetInPlace(carry, why); return }
    onReload(carry, positionNow())
  }
  const session = useLargeCsvSession(fileId, editable ? summary.contentHash : null, {
    enabled: editable,
    room: room ?? '',
    fragments: summary.fragments ?? [],
    header: summary.header,
    columnCount: summary.columnCount,
    dirty: summary.dirty,
    notice,
    onReload: reloadHere,
    generation: sessionGen,
    ...(gaveUp ? { ended: 'disconnected' as const } : {}),
  })
  const sessionRef = useRef<LargeCsvSessionApi | null>(session)
  sessionRef.current = session
  const edit = useLargeCsvEditing(gridRef, session, fileId, fileName, parentId, summary, swapping !== null)

  // #981 QA 14: this session's unsaved edits, told to the host (the server's rows can lag a fresh edit by the persist
  // debounce; this client's unacknowledged edits mark `dirty` at once)
  const unsaved = !!session?.dirty
  const onUnsavedRef = useRef(onUnsavedChange)
  onUnsavedRef.current = onUnsavedChange
  useEffect(() => { onUnsavedRef.current?.(unsaved) }, [unsaved])

  // A save this client hears of (its own or a collaborator's): reads that meet it wait for the swap.
  const sessionSaving = session?.saving ?? false
  useEffect(() => { if (sessionSaving) adapter.holdForSave() }, [sessionSaving, adapter])

  /** The swap ends once the next session is ready and the rows on screen are its own (see `awaitingRows`). */
  const endSwapIfCurrent = () => {
    const w = awaitingRows.current
    if (!w || w.id !== currentSwap.current?.id || !mounted.current) return
    if (editable && !sessionRef.current?.ready) return
    const api = gridRef.current?.api
    if (api && !api.isDestroyed() && !visibleRowsCurrent(api, summaryRef.current.contentHash, editable ? sessionRef.current?.readRows : undefined)) return
    endSwap(w.id)
  }
  abandonSwapRef.current = abandonSwap
  const endSwapRef = useRef(endSwapIfCurrent)
  endSwapRef.current = endSwapIfCurrent

  // The swap's next session is in place: re-read the blocks (the top row's index kept); the swap ends when they are in.
  const sessionKey = session?.readRows
  const continueSwap = () => {
    const w = awaitingSession.current
    if (!w) return
    const key = sessionRef.current?.readRows
    if (editable && (!key || key === w.from)) return
    awaitingSession.current = null
    const api = gridRef.current?.api
    if (api && !api.isDestroyed()) {
      if (w.clearStale) edit.refreshView()
      else api.refreshInfiniteCache()
      if (w.purge) api.purgeInfiniteCache()
      showRowAtTop(api, w.row)
    }
    if (w.id !== currentSwap.current?.id) return
    awaitingRows.current = { id: w.id }
    if (rowsBound.current) clearTimeout(rowsBound.current)
    rowsBound.current = setTimeout(() => { rowsBound.current = null; abandonSwapRef.current(w.id) }, SWAP_ROWS_BOUND_MS)
    if (sessionRef.current?.ended) { abandonSwap(w.id); return }
    endSwapIfCurrent()
  }
  const continueSwapRef = useRef(continueSwap)
  continueSwapRef.current = continueSwap
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the session (or the content) changes
  useEffect(() => { continueSwap() }, [sessionKey, summary, editable])
  // A save's swap with a view: its next session is up — join the new base's fragments of the rows on screen now.
  useEffect(() => {
    const p = prejoin.current
    const s = sessionRef.current
    if (!p || p.id !== currentSwap.current?.id || !s || !sessionKey || sessionKey === p.from) return
    prejoin.current = null
    if (p.frags.length) void s.joinFragments(p.frags)
  }, [sessionKey])

  // … and when the new session becomes ready; a re-read that has not come back is asked again (never stuck silently:
  // the edit bar says "Saving…" / "Updating…" for as long as the swap lasts).
  const sessionReady = session?.ready ?? false
  useEffect(() => { endSwapIfCurrent() }, [sessionReady]) // eslint-disable-line react-hooks/exhaustive-deps
  // the new session ended (forbidden / deleted / disconnected) while the swap waited on it: it can't finish here
  const sessionEnded = session?.ended ?? null
  useEffect(() => { if (sessionEnded && awaitingRows.current) abandonSwap(awaitingRows.current.id) }, [sessionEnded]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!swapping) return
    const t = setInterval(() => {
      if (!awaitingRows.current) return
      const api = gridRef.current?.api
      if (api && !api.isDestroyed()) api.refreshInfiniteCache()
    }, SWAP_REREAD_MS)
    return () => clearInterval(t)
  }, [swapping])
  const rowTotal = session?.totalRows ?? summary.totalRows
  const [viewProgress, setViewProgress] = useState<number | null>(null)
  /** The active view's row count; null = the file's own order. */
  const [viewRows, setViewRows] = useState<number | null>(null)
  const [headerMenu, setHeaderMenu] = useState<{ x: number; y: number; colId: string } | null>(null)
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; data: GridRow } | null>(null)
  /** The sort / filter view could not be built. */
  const [viewFailed, setViewFailed] = useState(false)
  /** Going to a search hit cleared the sort / filter (hits are in the file's own order). */
  const [viewCleared, setViewCleared] = useState(false)

  const { setViewActive, setProvisionalView } = edit
  const datasource = useMemo(
    () => largeCsvDatasource(
      adapter,
      totalRowsRef,
      setViewProgress,
      (v, current) => {
        setViewFailed(false)
        if (v) setViewCleared(false)
        setViewRows(v ? v.rowCount : null)
        setViewActive(!!v)
        // the old order after a save (#981 Task 9) is said while the latest read shows it; a real view (or none) ends it
        if (current) setProvisionalView(!!v?.provisional)
      },
      () => setViewFailed(true),
      editable ? () => sessionRef.current : undefined,
      () => endSwapRef.current(),
      () => {
        const pending = pendingViewSpec()
        if (pending !== undefined) return { spec: pending, pending: true }
        const at = positionNow()
        return at ? { spec: gridModelsToViewSpec(at.sortModel, at.filterModel as Record<string, unknown> | null), pending: false } : undefined
      },
    ),
    [adapter, editable, setViewActive, setProvisionalView],
  )

  const filterOptions = useMemo(() => [
    'contains', 'equals', 'startsWith', 'notBlank',
    { displayKey: 'numEq', displayName: t('largeCsv.filter.numEq'), predicate: (): boolean => true, numberOfInputs: 1 as const },
    { displayKey: 'numLt', displayName: t('largeCsv.filter.numLt'), predicate: (): boolean => true, numberOfInputs: 1 as const },
    { displayKey: 'numGt', displayName: t('largeCsv.filter.numGt'), predicate: (): boolean => true, numberOfInputs: 1 as const },
    { displayKey: 'numBetween', displayName: t('largeCsv.filter.numBetween'), predicate: (): boolean => true, numberOfInputs: 2 as const },
  ], [t])

  // The session's columns (display order → slot) while editing, else the manifest header. A column's id is its SLOT:
  // a source column's slot is its index (the server's sort / filter / search address it); an added one can't be sorted.
  const columns = session?.columns
  const columnsKey = JSON.stringify(columns ?? summary.header)
  const { setCellValue, canEditRef } = edit
  /** What the find bar marks: the searched text in every cell on screen, and the current hit (file order only). */
  const findMarkRef = useRef<FindMark>({ query: '', active: null })
  const colDefs = useMemo<ColDef<GridRow>[]>(() => {
    const cols = columns ?? summary.header.map((name, i) => ({ slot: String(i), name }))
    return cols.map((c, i) => {
      const source = /^\d+$/.test(c.slot)
      return {
        colId: c.slot,
        headerName: c.name || `#${i + 1}`,
        valueGetter: (p) => p.data?.fields[i] ?? '',
        cellRenderer: (p: ICellRendererParams<GridRow>) => {
          const { query: q, active } = findMarkRef.current
          const value = String(p.value ?? '')
          if (!q) return value
          const isActive = !!active && p.node.rowIndex === active.row && p.colDef?.colId === active.col
          return <FindMarks value={value} query={q} active={isActive} />
        },
        valueSetter: (p) => setCellValue(p.data, i, p.colDef.colId ?? c.slot, p.newValue),
        // a row still held from an older content (a save swapped the session meanwhile) is read-only until re-read
        editable: (p) => editable && canEditRef.current && !p.data?.deleted && rowIsCurrent(p.data, summaryRef.current.contentHash),
        sortable: source,
        resizable: true,
        filter: source ? 'agTextColumnFilter' : false,
        filterParams: { filterOptions, maxNumConditions: 1, buttons: ['apply', 'reset'], closeOnApply: true },
        pinned: frozen.has(c.slot) ? ('left' as const) : null,
      } satisfies ColDef<GridRow>
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `columnsKey` stands for `columns`
  }, [columnsKey, filterOptions, frozen, editable, setCellValue, canEditRef])

  // #981 Task 9: the new base's columns are in — the sort / filter / frozen columns move to their new ids
  useEffect(() => {
    const p = pendingColumns.current
    const api = gridRef.current?.api
    if (!p || !api || api.isDestroyed()) return
    if (colDefs.length !== p.count || colDefs.some((c, i) => c.colId !== String(i))) return
    pendingColumns.current = null
    setFrozen(new Set(p.at.frozen))
    api.applyColumnState({ state: p.at.sortModel.map((m, i) => ({ colId: m.colId, sort: m.sort, sortIndex: i })), defaultState: { sort: null } })
    if (JSON.stringify(api.getFilterModel() ?? null) !== JSON.stringify(p.at.filterModel)) api.setFilterModel(p.at.filterModel)
  }, [colDefs])

  // ── find bar ──
  const [showFind, setShowFind] = useState(false)
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<LargeCsvCell[]>([])
  const [total, setTotal] = useState<{ n: number; capped: boolean } | null>(null)
  const [searchFailed, setSearchFailed] = useState(false)
  const [hitIndex, setHitIndex] = useState(-1)
  const [searchProgress, setSearchProgress] = useState<number | null>(null)
  const searchedRef = useRef<string | null>(null)
  const findRef = useRef<HTMLInputElement>(null)

  // Marks follow a finished search (not each keystroke) and the hit in view; a sort / filter view has no "current hit".
  const viewActive = viewRows !== null
  useEffect(() => {
    const q = total !== null && searchedRef.current ? searchedRef.current : ''
    const hit = hitIndex >= 0 ? hits[hitIndex] : undefined
    findMarkRef.current = { query: q, active: hit && !viewActive ? { row: hit.row, col: String(hit.col) } : null }
    const api = gridRef.current?.api
    if (api && !api.isDestroyed()) api.refreshCells({ force: true })
  }, [total, hits, hitIndex, viewActive])

  // Cmd/Ctrl+F anywhere while this grid is on screen opens ITS find bar (as the small grid does), not the browser's.
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isFindShortcut(e) || !rootRef.current || !isShown(rootRef.current)) return
      e.preventDefault()
      setShowFind(true)
      requestAnimationFrame(() => findRef.current?.focus())
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const resetFind = useCallback(() => {
    searchedRef.current = null
    setHits([])
    setTotal(null)
    setHitIndex(-1)
    setSearchProgress(null)
    setSearchFailed(false)
    setViewCleared(false)
  }, [])

  const goToHit = useCallback((hit: LargeCsvCell) => {
    const api = gridRef.current?.api
    if (!api) return
    // Hits are positions in the file's own order: leave a sort / filter view first.
    const sorted = api.getColumnState().some((c) => c.sort)
    const filtered = api.isAnyFilterPresent()
    if (sorted) api.applyColumnState({ defaultState: { sort: null } })
    if (filtered) api.setFilterModel(null)
    if (sorted || filtered) setViewCleared(true)
    api.ensureIndexVisible(hit.row, 'middle')
    api.ensureColumnVisible(String(hit.col))
    api.setFocusedCell(hit.row, String(hit.col))
  }, [])

  const step = useCallback(async (dir: 1 | -1) => {
    const q = query.trim()
    // positions found now would be the content being replaced
    if (!q || swappingRef.current) return
    let list = hits
    let idx = hitIndex
    if (searchedRef.current !== q) {
      searchedRef.current = q
      setSearchProgress(0)
      const r = await adapter.search(q, undefined, setSearchProgress).catch(() => ({ failed: 'error' as const }))
      setSearchProgress(null)
      if (searchedRef.current !== q) return
      if ('failed' in r) {
        // Asked again on the next Enter.
        searchedRef.current = null
        setTotal(null); setHits([]); setHitIndex(-1); setSearchFailed(true)
        return
      }
      setSearchFailed(false)
      list = r.hits
      setHits(list)
      setTotal({ n: r.total, capped: r.totalCapped })
      idx = -1
    }
    let next = idx + dir
    if (dir === 1 && next >= list.length && list.length > 0) {
      // the loaded page is used up: the next page after the last hit
      const r = await adapter.search(q, list[list.length - 1]).catch(() => ({ failed: 'error' as const }))
      if (searchedRef.current !== q) return
      if ('failed' in r) {
        setSearchFailed(true)
        return
      }
      setSearchFailed(false)
      if (r.hits.length) {
        list = [...list, ...r.hits]
        setHits(list)
      } else {
        next = 0 // wrap to the first
      }
    }
    if (next < 0) next = list.length - 1
    if (next < 0 || next >= list.length) return
    setHitIndex(next)
    goToHit(list[next])
  }, [query, hits, hitIndex, adapter, goToHit])

  const onFindKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void step(e.shiftKey ? -1 : 1) }
    if (e.key === 'Escape') { setShowFind(false); setQuery(''); resetFind() }
  }

  // A right-click on a column header: freeze / unfreeze it (as the small grid's header menu does) and, while
  // editing, its column ops; on a row: insert / delete rows.
  const onGridContextMenu = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const cell = target.closest?.('.ag-header-cell[col-id]')
    const colId = cell?.getAttribute('col-id')
    if (colId) {
      e.preventDefault()
      setHeaderMenu({ x: e.clientX, y: e.clientY, colId })
      return
    }
    if (!editable) return
    const rowEl = target.closest?.('.ag-row[row-index]')
    const idx = Number(rowEl?.getAttribute('row-index'))
    const data = Number.isInteger(idx) ? gridRef.current?.api?.getDisplayedRowAtIndex(idx)?.data : undefined
    if (!data) return
    e.preventDefault()
    setRowMenu({ x: e.clientX, y: e.clientY, data })
  }, [editable])

  useEffect(() => {
    if (!headerMenu && !rowMenu) return
    const close = () => { setHeaderMenu(null); setRowMenu(null) }
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [headerMenu, rowMenu])

  // Cmd/Ctrl+S inside the grid saves an edited file (Cmd/Ctrl+F: the window listener above).
  const onGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 's' && session) {
      e.preventDefault()
      if (session.dirty && edit.canSave && !session.savePending) session.save()
    }
  }

  return (
    <div ref={rootRef} className="flex-1 flex flex-col overflow-hidden" data-testid="large-csv-grid" onKeyDown={onGridKeyDown}>
      <div className="h-10 border-b themed-border flex items-center px-4 gap-2 shrink-0 themed-bg-secondary">
        {showFind ? (
          <div className="flex items-center gap-1.5 flex-1">
            <Search size={14} className="themed-text-muted shrink-0" />
            <input
              ref={findRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); resetFind() }}
              onKeyDown={onFindKeyDown}
              placeholder={t('largeCsv.findPlaceholder')}
              className="flex-1 bg-transparent text-sm themed-text focus:outline-none min-w-0"
              data-testid="large-csv-find-input"
              autoFocus
            />
            {swapping && (
              <span className="text-xs themed-text-muted whitespace-nowrap" data-testid="large-csv-find-paused">
                {swapping === 'save' ? t('largeCsv.findPaused') : t('largeCsv.findPausedUpdating')}
              </span>
            )}
            <span className="text-xs themed-text-muted whitespace-nowrap" data-testid="large-csv-find-status">
              {swapping ? '' : searchProgress !== null
                ? t('largeCsv.searching', { percent: percent(searchProgress) })
                : searchFailed
                  ? t('largeCsv.searchFailed')
                  : total === null
                  ? ''
                  : total.n === 0
                    ? t('largeCsv.noMatches')
                    : t(total.capped ? 'largeCsv.matchOfCapped' : 'largeCsv.matchOf', { current: (hitIndex + 1).toLocaleString(), total: total.n.toLocaleString() })}
            </span>
            <button onClick={() => void step(-1)} disabled={!query.trim()} className="themed-text-muted hover:themed-text disabled:opacity-30 p-0.5"
              title={`${t('largeCsv.previous')} (${formatShortcut({ key: 'Enter', shift: true })})`}>
              <ChevronUp size={14} />
            </button>
            <button onClick={() => void step(1)} disabled={!query.trim()} className="themed-text-muted hover:themed-text disabled:opacity-30 p-0.5"
              title={`${t('largeCsv.next')} (${formatShortcut({ key: 'Enter' })})`}>
              <ChevronDown size={14} />
            </button>
            <button onClick={() => { setShowFind(false); setQuery(''); resetFind() }} className="themed-text-muted hover:themed-text p-0.5" title={t('largeCsv.closeFind')}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setShowFind(true); requestAnimationFrame(() => findRef.current?.focus()) }}
            className="flex items-center gap-1.5 px-2 py-1 text-sm rounded themed-text-muted hover:themed-bg-tertiary hover:themed-text transition-colors flex-1 min-w-0"
            title={`${t('largeCsv.find')} (${formatShortcut({ key: 'f', mod: true })})`}
            data-testid="large-csv-find"
          >
            <Search size={14} className="shrink-0" />
            <span className="truncate">{t('largeCsv.findPlaceholder')}</span>
          </button>
        )}
        {viewUpdating !== null && (
          <span className="text-xs themed-text-muted whitespace-nowrap inline-flex items-center gap-1" data-testid="large-csv-view-updating">
            <Loader2 className="animate-spin" size={12} />
            {t('largeCsv.viewUpdating', { percent: percent(viewUpdating) })}
          </span>
        )}
        {viewProgress !== null && (
          <span className="text-xs themed-text-muted whitespace-nowrap inline-flex items-center gap-1" data-testid="large-csv-view-building">
            <Loader2 className="animate-spin" size={12} />
            {t('largeCsv.viewBuilding', { percent: percent(viewProgress) })}
          </span>
        )}
        {viewFailed && viewProgress === null && (
          <span className="text-xs text-red-400 whitespace-nowrap inline-flex items-center gap-1" role="alert" data-testid="large-csv-view-failed">
            <AlertCircle size={12} />
            {t('largeCsv.viewFailed')}
          </span>
        )}
        {viewCleared && !viewFailed && (
          <span className="text-xs themed-text-muted whitespace-nowrap" data-testid="large-csv-view-cleared">
            {t('largeCsv.viewCleared')}
          </span>
        )}
        {edit.viewStale && (
          <span className="text-xs themed-text-muted inline-flex items-center gap-1.5 min-w-0" data-testid="large-csv-view-stale">
            <span className="truncate" title={t('largeCsv.edit.viewStale')}>{t('largeCsv.edit.viewStale')}</span>
            <button
              onClick={() => { datasource.forgetViews(); edit.refreshView() }}
              className="px-2 py-0.5 rounded themed-btn-secondary whitespace-nowrap"
              data-testid="large-csv-refresh-view"
            >
              {t('largeCsv.edit.refreshView')}
            </button>
          </span>
        )}
        <span className="text-xs themed-text-muted whitespace-nowrap">
          {viewRows !== null && viewRows < rowTotal
            ? t('largeCsv.rowsOf', { shown: viewRows.toLocaleString(), total: rowTotal.toLocaleString() })
            : t('largeCsv.rows', { total: rowTotal.toLocaleString() })}
        </span>
        {session && (
          <LargeCsvEditBar
            session={session}
            message={edit.message}
            onSaveCopy={edit.saveCopy}
            onRetryCopy={edit.retryCopy}
            copyBusy={edit.copyBusy}
            copyMessage={edit.copyMessage}
            onReopen={onReopen}
            swapping={swapping}
          />
        )}
      </div>

      {session && (
        <LargeCsvStaleEditsBar bases={edit.staleBases} busy={edit.copyBusy || !edit.canSave}
          onSaveCopy={(b) => { void edit.saveStaleCopy(b).then(() => onStaleEditsChanged?.(), () => { /* the copy message says why */ }) }}
          onDiscard={(b) => { edit.discardStale(b); onStaleEditsChanged?.() }} />
      )}

      {/* #1315 A: why editing stopped — the session's file state, else (no session) the read-only grid's */}
      <FileStateBanner state={session ? session.fileState : readOnlyBanner} />

      <div className="flex-1 min-h-0 relative" onContextMenu={onGridContextMenu}>
        {viewProgress !== null && (
          // A sort / filter is read from a view built over the whole file: until it exists the grid has no rows to
          // show, so it says what it is doing over the empty body (the toolbar's figure is easy to miss).
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" data-testid="large-csv-view-building-overlay">
            <div className="themed-bg-secondary border themed-border rounded shadow-lg px-4 py-3 text-sm themed-text inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={16} />
              {t('largeCsv.viewBuilding', { percent: percent(viewProgress) })}
            </div>
          </div>
        )}
        <AgGridReact<GridRow>
          ref={gridRef}
          theme={dataGridTheme}
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={BLOCK_SIZE}
          maxBlocksInCache={BLOCKS_CACHED}
          maxConcurrentDatasourceRequests={2}
          // The count is known at open: the grid has it before its first block arrives, and again after a view
          // change purges its blocks (AG Grid re-reads this then; `datasource.rowCount` it ignores). Counted as one
          // block, End pressed while the first block was loading (the re-open after Save, on a slow edge) stopped at
          // row 200, and a find hit past it after leaving a view was "an invalid row index". Each block's answer
          // still sets the live count (a view's, an edited file's).
          infiniteInitialRowCount={initialRowCount}
          rowBuffer={ROW_BUFFER}
          initialState={initialGridState}
          onFirstDataRendered={(e) => {
            if (!opensAt || opensAt.row <= 0) return
            showRowAtTop(e.api, opensAt.row)
          }}
          // the person's own sort / filter replaces a renumbering not applied yet (#981 Task 9)
          onSortChanged={(e) => { if (viewChangeScrollsToTop(e.source)) { pendingColumns.current = null; e.api.ensureIndexVisible(0, 'top') } }}
          onFilterChanged={(e) => { if (viewChangeScrollsToTop(e.source)) { pendingColumns.current = null; e.api.ensureIndexVisible(0, 'top') } }}
          columnDefs={colDefs}
          getRowId={(p) => String(p.data.row)}
          rowClassRules={ROW_CLASS_RULES}
          animateRows={false}
          enableCellTextSelection
          preventDefaultOnContextMenu
        />
      </div>

      {headerMenu && (
        <div
          className="fixed z-50 themed-bg-secondary border themed-border rounded shadow-lg py-1 text-sm"
          style={{ left: headerMenu.x, top: headerMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="large-csv-header-menu"
        >
          <button
            className="block w-full text-left px-3 py-1 themed-text hover:themed-bg-tertiary"
            onClick={() => {
              const colId = headerMenu.colId
              setFrozen((prev) => {
                const nextSet = new Set(prev)
                if (nextSet.has(colId)) nextSet.delete(colId)
                else nextSet.add(colId)
                return nextSet
              })
              setHeaderMenu(null)
            }}
          >
            {frozen.has(headerMenu.colId) ? t('largeCsv.unfreezeColumn') : t('largeCsv.freezeColumn')}
          </button>
          {session && edit.canEdit && (
            <LargeCsvColumnMenuItems
              session={session}
              slot={headerMenu.colId}
              onDone={(r) => { setHeaderMenu(null); if (r) edit.columnOp(r) }}
            />
          )}
        </div>
      )}

      {rowMenu && session && (
        <LargeCsvRowMenu
          x={rowMenu.x}
          y={rowMenu.y}
          canEdit={edit.canEdit}
          inView={edit.viewActive}
          deleted={!!rowMenu.data.deleted}
          onInsert={(where) => { edit.insertRows(rowMenu.data, where); setRowMenu(null) }}
          onDelete={() => { edit.deleteRow(rowMenu.data); setRowMenu(null) }}
        />
      )}
    </div>
  )
}
